/**
 * absencePlannerAdapter.js
 *
 * Transforms tidsregistering domain objects into the Category[] shape
 * expected by AbsencePlannerChart (day-granularity timeline).
 *
 * Input:
 *   absences         — Absence[]         from useAbsences
 *   staffMembers     — StaffMember[]     ordered list for row display
 *   registrationCodes — RegistrationCode[]  for color + label lookup
 *
 * Output: Category[] where each Category is one staff member row and each
 * slot is one absence bar.
 */

import { CATEGORY_COLORS, DEFAULT_COLOR } from '../constants/colors';

function resolveColor(registrationCode) {
  if (!registrationCode) return DEFAULT_COLOR;
  return CATEGORY_COLORS[registrationCode.statistical_category] ?? DEFAULT_COLOR;
}

/**
 * @param {object[]} absences
 * @param {object[]} staffMembers
 * @param {object[]} registrationCodes
 * @returns {object[]} Category[]
 */
export function absencesToCategories(absences, staffMembers, registrationCodes) {
  const codeMap = new Map(registrationCodes.map((rc) => [rc.registration_code_id, rc]));

  // Group absences by staff_member_id
  const absencesByStaff = new Map();
  for (const absence of absences) {
    const list = absencesByStaff.get(absence.staff_member_id) ?? [];
    list.push(absence);
    absencesByStaff.set(absence.staff_member_id, list);
  }

  return staffMembers.map((staffMember) => {
    const staffAbsences = absencesByStaff.get(staffMember.staff_member_id) ?? [];

    const slots = staffAbsences.map((absence) => {
      const registrationCode = codeMap.get(absence.registration_code_id) ?? null;
      return {
        id: absence.absence_id,
        // Day-granularity: use midnight Date objects so the timeline positions correctly
        startDate: new Date(absence.start_date),
        endDate: new Date(absence.end_date),
        color: resolveColor(registrationCode),
        approvalState: absence.approval_state,
        registrationCode,
        originalAbsence: absence,
      };
    });

    return {
      id: staffMember.staff_member_id,
      name: staffMember.name,
      staffMember,
      // tasks is the field the timeline layout reads for height calculations
      tasks: slots,
    };
  });
}
